<template>
  <div class="landing-page">
    <!-- Sticky Header (appears after scrolling) -->
    <header class="sticky-header" :class="{ 'visible': showStickyHeader }">
      <div class="header-content">
        <PassingOnLogo class="header-logo" />
        <v-btn
          color="primary"
          rounded="0"
          elevation="0"
          @click="startMemorial"
        >
          Get Started
        </v-btn>
      </div>
    </header>

    <!-- Section 1: Hero Grid -->
    <section class="hero-section">
      <!-- Grid background — hero center is an embedded grid cell -->
      <div class="grid-bg" :style="gridStyle">
        <!-- Hero center panel - explicitly placed in grid -->
        <div class="hero-center" :style="heroCenterStyle">
          <PassingOnLogo class="hero-logo" tone="hero" :rotate="true" :stack="true" align="center" />
        <p class="hero-tagline mb-4">Create a digital memory for your loved one</p>

        <div class="intro-offer-banner mb-5">
          <v-icon size="24" aria-hidden="true">mdi-gift-outline</v-icon>
          <span>Free introductory offer — no credit card required</span>
        </div>

        <div class="hero-actions">
          <template v-if="isLoggedIn">
            <v-btn
              color="primary"
              size="large"
              rounded="0"
              elevation="0"
              class="px-8 py-2 hero-btn-solid"
              @click="startNewMemorial"
            >
              Start a New Memory
            </v-btn>
            <v-btn
              variant="outlined"
              color="primary"
              size="default"
              rounded="0"
              class="px-6 py-2 hero-btn-ghost"
              @click="continueExistingMemorial"
            >
              Continue Memory
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              color="primary"
              size="large"
              rounded="0"
              elevation="0"
              class="px-8 py-2 hero-btn-solid"
              @click="startMemorial"
            >
              Get Started
            </v-btn>
            <v-btn
              variant="text"
              color="primary"
              size="small"
              class="sign-in-button mt-4 hero-btn-ghost"
              @click="goToLogin"
            >
              Already Have an Account? Sign In
            </v-btn>
          </template>
        </div>
        </div>
        <!-- Photo cells — auto-placed around hero center panel -->
        <div v-for="cell in gridCells" :key="cell.slotIndex" class="grid-cell">
          <transition name="grid-fade">
            <img v-if="cell.type === 'photo'" :key="'p-' + cell.key" :src="cell.content" alt="Memory" />
            <div v-else :key="'q-' + cell.key" class="grid-quote">"{{ cell.content }}"</div>
          </transition>
        </div>
      </div>

      <!-- Scroll indicator -->
      <div class="scroll-indicator" @click="scrollToSection('what-section')">
        <v-icon>mdi-chevron-down</v-icon>
      </div>
    </section>

    <!-- Section 2: What is PassingOn? -->
    <section id="what-section" class="what-section">
      <v-container class="what-container">
        <v-row>
          <!-- Left side: Header, text, and overlapping photo/quote -->
          <v-col cols="12" class="what-left-content">
            <!-- Header at top - left aligned -->
            <div class="what-header">
              <h2 class="section-title">What is <span class="brand-n1-dark">{{ COMPANY_NAME_1 }}</span><span class="brand-n2-red">{{ COMPANY_NAME_2 }}</span>?</h2>
              <p class="what-intro">
                <span class="brand-n1-dark">{{ COMPANY_NAME_1 }}</span><span class="brand-n2-red">{{ COMPANY_NAME_2 }}</span> is an online memory and funeral-planning platform. It helps families and friends celebrate a life by bringing memories, photos, stories, and event details together in one secure, private space.
              </p>
              <p>
                <span>Beyond goodbye</span>
              </p>
            </div>

            <!-- Full-width 6×2 photo grid -->
            <div class="what-overlap-wrapper">
              <div class="what-photo-grid">
                <div v-for="i in 12" :key="i" class="photo-grid-cell">
                  <img v-if="whatPhotos[i - 1]" :src="whatPhotos[i - 1]" alt="Memory" />
                </div>
              </div>
            </div>
          </v-col>

          <!-- Right side: hidden for now -->
          <v-col v-if="false" cols="12" md="6" class="what-right-content">
            <div class="tablet-mockup-angled">
              <div class="tablet-frame">
                <div class="tablet-screen">
                  <img src="/images/active1/images/active1/forever_x_01.jpg" alt="Memory Preview" class="tablet-content" />
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- Scroll indicator -->
      <div class="scroll-indicator" @click="scrollToSection('how-section')">
        <v-icon>mdi-chevron-down</v-icon>
      </div>
    </section>

    <!-- Section 3: How Does It Work? -->
    <section id="how-section" class="how-section">
      <v-container class="how-container">
        <v-row>
          <!-- Left side: Header, text, button, and features -->
          <v-col cols="12" md="7" class="how-left-content">
            <!-- Header at top - left aligned -->
            <div class="how-header">
              <h2 class="section-title">How does it work?</h2>
              <p class="how-intro">
                You can create a private memory page, invite contributors to share photos, videos, and messages, and plan funeral or memory details such as locations, maps, and times.
You can also download everything as a PDF or play a slideshow during the service.
              </p>
            </div>

            <!-- Start Here button -->
            <div class="start-here-wrapper mb-8">
              <v-btn
                color="primary"
                variant="flat"
                size="x-large"
                rounded="0"
                elevation="0"
                class="px-12 py-3 how-get-started-btn"
                @click="startMemorial"
              >
                Get Started
              </v-btn>
            </div>
          </v-col>

          <!-- Right side: Decorative photo and quote -->
          <v-col cols="12" md="5" class="how-right-content">
            <div class="how-overlap-wrapper">
              <div class="section-photo-grid">
                <div v-for="i in 6" :key="i" class="photo-grid-cell">
                  <img v-if="howPhotos[i - 1]" :src="howPhotos[i - 1]" alt="Memory" />
                </div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Three feature areas in their own row -->
        <v-row class="feature-row mt-8">
          <v-col cols="12" sm="4" class="feature-col">
            <div class="feature-item feature-item-square">
              <div class="feature-icon">
                <v-icon size="48" color="primary">mdi-heart-outline</v-icon>
              </div>
              <h3 class="feature-title">Create a Digital Memory</h3>
              <p class="feature-description">
                Quickly create a beautiful lasting tribute to celebrate the life and memories of your loved one
              </p>
            </div>
          </v-col>
          <v-col cols="12" sm="4" class="feature-col">
            <div class="feature-item feature-item-square">
              <div class="feature-icon">
                <v-icon size="48" color="primary">mdi-account-plus-outline</v-icon>
              </div>
              <h3 class="feature-title">Invite family & friends to contribute</h3>
              <p class="feature-description">
                Invite others to add their own photos, stories and cherished memories
              </p>
            </div>
          </v-col>
          <v-col cols="12" sm="4" class="feature-col">
            <div class="feature-item feature-item-square">
              <div class="feature-icon">
                <v-icon size="48" color="primary">mdi-share-variant-outline</v-icon>
              </div>
              <h3 class="feature-title">Share online or print</h3>
              <p class="feature-description">
                Share your memory online or create a slideshow
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- Scroll indicator -->
      <div class="scroll-indicator" @click="scrollToSection('faq-section')">
        <v-icon>mdi-chevron-down</v-icon>
      </div>
    </section>

    <!-- Section 4: Frequently Asked Questions -->
    <section id="faq-section" class="faq-section">
      <v-container class="faq-container">
        <v-row>
          <!-- Left side: FAQ Accordion -->
          <v-col cols="12" md="7" class="faq-left-content">
            <div class="faq-header">
              <h2 class="section-title">Frequently Asked Questions</h2>
            </div>

            <v-expansion-panels variant="accordion" class="faq-panels">
              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">What is <span class="brand-n1-dark">{{ COMPANY_NAME_1 }}</span><span class="brand-n2-red">{{ COMPANY_NAME_2 }}</span>?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    <span class="brand-n1-dark">{{ COMPANY_NAME_1 }}</span><span class="brand-n2-red">{{ COMPANY_NAME_2 }}</span> is where a life becomes a story worth keeping. It's an online memory that brings together memories, photographs, and moments — permanent and shaped around what made someone irreplaceable.<br /><br />
                    The '-x' is a placeholder. Replace it with the quality they lived by, or simply their name. Forever-kind. Forever-wild. Forever-brave. Forever-Sarah. The memory becomes theirs.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">How does it work?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    You create a memory page, share it with the people who mattered, and together you build something real: photos, videos, stories, funeral details, maps, times. Export everything as a PDF. Play a slideshow at the service. Keep it all in one place that doesn't disappear.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Who can use <span class="brand-n1-dark">{{ COMPANY_NAME_1 }}</span><span class="brand-n2-red">{{ COMPANY_NAME_2 }}</span>?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Anyone. The person who creates the memory controls what stays visible.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Does it cost anything to create a memory?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Creating a memory is free. You pay when you’re ready to share it and open it to contributors.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">How long does a memory stay online?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Twelve months from activation. After that, it switches to a low monthly subscription you can cancel anytime — and anyone who contributed can choose to sponsor it. Memories aren’t meant to fade.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">What happens if I cancel the subscription?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    You can cancel anytime. Once cancelled, you’ll have 90 days to reactivate before the memory and everything in it gets permanently deleted. We’ll send three reminders during that window.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">How do I pay?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    There is currently a free introductory offer for people who sign up now, so no payment or credit card is required. After the introductory offer ends, payments will run through Stripe and all major credit and debit cards will be accepted. We never see or store your card details.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Is my memory private?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Memories are accessible to anyone who has the web address. When you share the link with family and friends — by email, text, or social media — those people can view it and contribute. If they share it further, those people can access it too.<br /><br />
                    We designed it this way to make contributing effortless. No login required, no barriers between someone wanting to share a memory and actually doing it.<br /><br />
                    You control what stays visible. You can review contributions before they go live, and you can remove anything you don't want on the memory.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Who can upload photos or videos?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Anyone with the memory link can contribute photos, videos, and stories. You can review and approve contributions before they appear publicly, or remove anything you don't want displayed.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Can I download the memories?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Yes. You can generate a PDF of all photos, comments, and stories whenever you want. For safekeeping, for sharing, for printing.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Can I play a slideshow at the funeral?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Yes. Every memory includes a built-in slideshow feature that you can display during the service or gathering.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Can people donate to help with funeral costs?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Yes. Memory creators can enable donations to help cover funeral expenses or memory costs. Donations go directly from the donor to the memory creator's bank account via Stripe — we never hold the funds.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">How do donations work?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    If you want to accept donations, you'll complete a quick onboarding with Stripe (our payment processor). Donors contribute directly to your connected account. We charge a 1% platform fee on top of Stripe's standard processing fees.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Are donations tax-deductible?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    No. Donations to personal memories are not tax-deductible. Only donations to DGR-endorsed registered charities qualify for tax deductions in Australia.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Can I get a refund on a donation?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Refunds aren't available once a donation has been processed, except where required by law. Donations go directly to the memory creator, not to us.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Can I delete a memory?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Yes. The memory owner can delete it anytime. Deleted memories are held in secure backup for 90 days before being permanently erased.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Can I get my data back after deletion?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Yes — within the 90-day window, you can contact us to restore it.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">What happens if I forget my password?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Use the 'Forgot Password' link on the login screen to reset it securely via email.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">What if I need help or have feedback?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    We don’t offer live technical support, but we want to hear from you. If something breaks, if you have an idea, or if you just want to tell us what worked or didn’t — use our Help & Feedback form. We read every submission and use them to shape what comes next.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Is my information secure?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Yes. We use industry-standard encryption and AWS cloud hosting. Payments are handled by Stripe — we never see or store your card details.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Do you sell or share my data?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Never. We use your information only to run the service, send product updates, and stay in touch. If The Fourth Seagull Pty Ltd is ever sold or merged, your data transfers to the new owner under the same privacy safeguards.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title>
                  <span class="faq-question">Can I unsubscribe from emails?</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="faq-answer">
                    Opt out of marketing or update emails anytime using the “unsubscribe" link or by updating your profile settings.
                  </p>
                </v-expansion-panel-text>
              </v-expansion-panel>

            </v-expansion-panels>
          </v-col>

          <!-- Right side: Decorative photo and text -->
          <v-col cols="12" md="5" class="faq-right-content">
            <div class="faq-overlap-wrapper">
              <div class="section-photo-grid">
                <div v-for="i in 6" :key="i" class="photo-grid-cell">
                  <img v-if="faqPhotos[i - 1]" :src="faqPhotos[i - 1]" alt="Memory" />
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- Scroll indicator -->
      <div class="scroll-indicator" @click="scrollToTop">
        <v-icon>mdi-chevron-up</v-icon>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import { useStartMemorial } from '../composables/useStartMemorial'
import { COMPANY_NAME_1, COMPANY_NAME_2 } from '../config/branding'

const router = useRouter()
const store = useMemorialStore()

// Sticky header visibility
const showStickyHeader = ref(false)

// Check if user is logged in; entry points into the creation flow
const { isLoggedIn, startMemorial, startNewMemorial } = useStartMemorial()

// Items for animated display
// -------- Images Only --------
/*
const images_orig = [
  { id: "image-1", type: "photo", content: "/images/examples/ex2.jpg", size: "large" },
  { id: "image-2", type: "photo", content: "/images/examples/ex3.jpg", size: "large" },
  { id: "image-3", type: "photo", content: "/images/examples/ex4.jpg", size: "large" },
  { id: "image-4", type: "photo", content: "/images/examples/ex5.jpg", size: "large" },
  { id: "image-5", type: "photo", content: "/images/examples/ex6.jpg", size: "large" },
  { id: "image-6", type: "photo", content: "/images/examples/ex7.jpg", size: "large" },
  { id: "image-7", type: "photo", content: "/images/examples/ex8.jpg", size: "large" },
  { id: "image-8", type: "photo", content: "/images/examples/ex9.jpg", size: "large" },
  { id: "image-9", type: "photo", content: "/images/examples/ex10.jpg", size: "large" },
  { id: "image-10", type: "photo", content: "/images/examples/ex11.jpg", size: "large" },
  { id: "image-11", type: "photo", content: "/images/examples/ex12.jpg", size: "large" },
  { id: "image-12", type: "photo", content: "/images/examples/ex13.jpg", size: "large" },
  { id: "image-13", type: "photo", content: "/images/examples/ex14.jpg", size: "large" },
  { id: "image-14", type: "photo", content: "/images/examples/ex15.jpg", size: "large" },
  { id: "image-15", type: "photo", content: "/images/examples/ex16.jpg", size: "large" },
  { id: "image-16", type: "photo", content: "/images/examples/ex17.jpg", size: "large" },
  { id: "image-17", type: "photo", content: "/images/examples/ex18.jpg", size: "large" },
  { id: "image-18", type: "photo", content: "/images/examples/ex19.jpg", size: "large" },
  { id: "image-19", type: "photo", content: "/images/examples/ex20.jpg", size: "large" },
  { id: "image-20", type: "photo", content: "/images/examples/ex21.jpg", size: "large" }
];
*/
const images = [
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_01.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_02.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_03.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_04.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_05.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_06.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_07.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_08.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_09.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_10.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_11.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_12.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_13.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_14.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_15.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_16.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_17.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_18.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_19.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_20.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_21.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_22.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_23.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_24.jpg", size: "large" },
{ id: "image-1", type: "photo", content: "/images/active1/forever_x_25.jpg", size: "large" }
];

// Photo grids for decorative sections (nulls are transparent gaps, no two gaps are orthogonally adjacent)
// what: 6×2 grid (12 cells), gaps at 0,5,9
const whatPhotos = [null, images[0].content, images[1].content, images[2].content, images[3].content, null, images[4].content, images[5].content, images[6].content, null, images[7].content, images[8].content]
const howPhotos = [null, images[3].content, images[4].content, images[5].content, null, images[6].content]
const faqPhotos = [images[4].content, null, images[5].content, null, images[6].content, images[7].content]

// -------- Notes Only (kept for future use) --------
// eslint-disable-next-line no-unused-vars
/*
const notes = [
  { id: "note-1", type: "note", content: "and then the dog just jumped into the canoe unexpectedly", size: "large" },
  { id: "note-2", type: "note", content: "we all burst out laughing when the cake collapsed sideways", size: "large" },
  { id: "note-3", type: "note", content: "you were wearing that ridiculous hat with the blinking lights", size: "large" },
  { id: "note-4", type: "note", content: "and the sky turned orange right as the music started", size: "large" },
  { id: "note-5", type: "note", content: "she handed me the note, folded exactly like in school", size: "large" },
  { id: "note-6", type: "note", content: "I still remember the smell of the rain that night", size: "large" },
  { id: "note-7", type: "note", content: "and somehow we ended up dancing barefoot in the kitchen", size: "large" },
  { id: "note-8", type: "note", content: "you yelled my name just before the fireworks went off", size: "large" },
  { id: "note-9", type: "note", content: "we sat on the roof eating chips and watching stars", size: "large" },
  { id: "note-10", type: "note", content: "his face lit up when he saw the old photo", size: "large" },
  { id: "note-11", type: "note", content: "we couldn't stop laughing after you tripped over the tent", size: "large" },
  { id: "note-12", type: "note", content: "she made that weird toast with jam and cheese again", size: "large" },
  { id: "note-13", type: "note", content: "you sang every word, even the ones you got wrong", size: "large" },
  { id: "note-14", type: "note", content: "and then the owl swooped right over our heads silently", size: "large" },
  { id: "note-15", type: "note", content: "we found that hidden beach with the smooth black stones", size: "large" },
  { id: "note-16", type: "note", content: "the kettle kept whistling while we argued about nothing important", size: "large" },
  { id: "note-17", type: "note", content: "you insisted the shortcut would save time and it absolutely didn’t", size: "large" },
  { id: "note-18", type: "note", content: "we painted the fence and ended up covered more than the wood", size: "large" },
  { id: "note-19", type: "note", content: "the lights flickered just as you started telling that ghost story", size: "large" },
  { id: "note-20", type: "note", content: "you tried to flip the pancake and it landed on the ceiling", size: "large" },
  { id: "note-21", type: "note", content: "we got caught in the sudden downpour and ran the whole way home", size: "large" },
  { id: "note-22", type: "note", content: "she kept pretending not to know the surprise was for her", size: "large" },
  { id: "note-23", type: "note", content: "the old radio crackled back to life at exactly the right moment", size: "large" },
  { id: "note-24", type: "note", content: "you waved like you’d won something when the train finally arrived", size: "large" },
  { id: "note-25", type: "note", content: "we stayed up far too late planning trips we never took", size: "large" },
  { id: "note-26", type: "note", content: "the neighbour’s cat adopted us for the entire weekend", size: "large" },
  { id: "note-27", type: "note", content: "you tried to teach me that card trick and forgot the ending", size: "large" },
  { id: "note-28", type: "note", content: "the candles melted into strange shapes before we noticed", size: "large" },
  { id: "note-29", type: "note", content: "we missed the exit and discovered the prettiest road instead", size: "large" },
  { id: "note-30", type: "note", content: "she whispered the punchline like it was a secret code", size: "large" },
  { id: "note-31", type: "note", content: "you balanced the entire stack of plates just to prove you could", size: "large" },
  { id: "note-32", type: "note", content: "the wind carried the music further than we expected", size: "large" },
  { id: "note-33", type: "note", content: "we argued over which cloud looked more like a dragon", size: "large" },
  { id: "note-34", type: "note", content: "you left the porch light on in case anyone wandered back late", size: "large" },
  { id: "note-35", type: "note", content: "the old clock chimed even though it hadn’t worked for years", size: "large" },
  { id: "note-36", type: "note", content: "we tried to build the shelf without reading the instructions", size: "large" },
  { id: "note-37", type: "note", content: "she laughed so hard she had to sit down on the pavement", size: "large" },
  { id: "note-38", type: "note", content: "you claimed you could taste the sea in the air that morning", size: "large" },
  { id: "note-39", type: "note", content: "we ended up talking to strangers like we’d known them for years", size: "large" },
  { id: "note-40", type: "note", content: "the fire crackled louder when someone mentioned your name", size: "large" },
  { id: "note-41", type: "note", content: "you tried to whistle the tune and startled the birds instead", size: "large" },
  { id: "note-42", type: "note", content: "we found the old ticket stubs tucked inside the book", size: "large" },
  { id: "note-43", type: "note", content: "she clapped first even though the show wasn’t quite finished", size: "large" },
  { id: "note-44", type: "note", content: "you pointed out the first star like it was a personal victory", size: "large" },
  { id: "note-45", type: "note", content: "we promised we’d remember the way it felt right then", size: "large" }
];
*/

// -------- Grid Logic --------
const gridCells = ref([])
const gridCols = ref(4)
const gridRows = ref(3)
let intervalId = null
let cellCounter = 0

const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${gridCols.value}, 1fr)`,
  gridTemplateRows: `repeat(${gridRows.value}, 1fr)`
}))

const centerSpan = computed(() => {
  if (gridCols.value <= 2) return 2
  if (gridCols.value === 3) return 1
  if (gridCols.value === 4) return 2
  return 3
})
const midRow = computed(() => Math.ceil(gridRows.value / 2))
const centerStartCol = computed(() => Math.floor((gridCols.value - centerSpan.value) / 2) + 1)
const heroCenterStyle = computed(() => ({
  gridRow: String(midRow.value),
  gridColumn: `${centerStartCol.value} / span ${centerSpan.value}`
}))

function pickUnused(usedContents) {
  const pool = images.filter(img => !usedContents.has(img.content))
  const source = pool.length > 0 ? pool : images
  return source[Math.floor(Math.random() * source.length)]
}

function createCell(slotIndex, usedContents) {
  // Notes kept in code but not used — swap `images` for a mixed pool to re-enable
  const item = pickUnused(usedContents)
  usedContents.add(item.content)
  return {
    slotIndex,
    key: cellCounter++,
    type: item.type,
    content: item.content
  }
}

function applyGrid() {
  const w = window.innerWidth
  const h = window.innerHeight
  gridCols.value = Math.min(5, Math.max(2, Math.floor(w / 300)))
  gridRows.value = Math.min(5, Math.max(3, Math.floor(h / 300)))
  const total = gridCols.value * gridRows.value - centerSpan.value
  const usedContents = new Set()
  gridCells.value = Array.from({ length: total }, (_, i) => createCell(i, usedContents))
}

function replaceRandomCell() {
  if (gridCells.value.length === 0) return
  const index = Math.floor(Math.random() * gridCells.value.length)
  const usedContents = new Set(gridCells.value.map(c => c.content))
  usedContents.delete(gridCells.value[index].content)
  const item = pickUnused(usedContents)
  const newCells = [...gridCells.value]
  newCells[index] = { slotIndex: index, key: cellCounter++, type: item.type, content: item.content }
  gridCells.value = newCells
}

// Handle scroll for sticky header
const handleScroll = () => {
  showStickyHeader.value = window.scrollY > 100
}

// Smooth scroll to section
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

// Scroll to top of page
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}


const continueExistingMemorial = () => {
  if (store.memorialUuid) {
    router.push(`/memory/load/${store.memorialUuid}`)
  } else {
    router.push('/profile')
  }
}

const goToLogin = () => {
  router.push('/login')
}

onMounted(() => {
  store.goToStep(0)
  window.addEventListener('scroll', handleScroll)
  window.addEventListener('resize', applyGrid)
  applyGrid()
  intervalId = setInterval(replaceRandomCell, 1000)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('resize', applyGrid)
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;

.landing-page {
  @include page-container;
  overflow-x: hidden;
}

// Sticky Header
.sticky-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transform: translateY(-100%);
  transition: transform 0.3s ease;

  &.visible {
    transform: translateY(0);
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 24px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .header-logo {
    height: 40px;
    width: auto;
  }
}

// Hero Section - full-viewport grid
.hero-section {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

.grid-bg {
  display: grid;
  width: 100%;
  height: 100%;
}

.grid-cell {
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.grid-quote {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  font-style: italic;
  background-color: $primary-very-light;
  color: var(--v-theme-gray600);
  font-size: clamp(12px, 1.2vw, 18px);
  font-family: 'Lora', serif;
}

// Grid cell fade transition
.grid-fade-enter-active,
.grid-fade-leave-active {
  transition: opacity 0.6s ease;
  position: absolute;
  inset: 0;
}
.grid-fade-enter-from,
.grid-fade-leave-to {
  opacity: 0;
}

// Hero Center — embedded grid cell with dark background
.hero-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: #1a1a1a;
  padding: 40px 50px;
}

.hero-logo {
  height: 45px;
  width: auto;
  margin-bottom: 12px;
}

.hero-tagline {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 10px;
}

.intro-offer-banner {
  display: inline-grid;
  grid-template-columns: auto minmax(0, auto);
  align-items: start;
  justify-content: center;
  column-gap: 16px;
  max-width: 100%;
  color: #D4401A;
  font-size: 1rem;
  line-height: 1.35;

  span {
    max-width: 30rem;
    text-align: center;
  }
}

.hero-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

// Hero panel button overrides — dark background panel only
.hero-btn-solid.v-btn {
  background-color: #D4401A !important;
  color: #ffffff !important;
  border-color: #D4401A !important;
}

.hero-btn-ghost.v-btn {
  background-color: transparent !important;
  color: #D4401A !important;
  border: 1px solid #D4401A !important;
}

.sign-in-button {
  white-space: normal;
  height: auto;
  line-height: 1.2;
  padding: 8px 16px;
  text-align: center;
}

@media (max-width: 600px) {
  .hero-center {
    padding: 24px 28px;
  }

  .intro-offer-banner {
    column-gap: 12px;
    font-size: 0.9rem;
  }
}

// Scroll Indicator
.scroll-indicator {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  animation: bounce 2s infinite;
  z-index: 25;

  .v-icon {
    font-size: 36px;
    color: $primary;
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  40% {
    transform: translateX(-50%) translateY(-10px);
  }
  60% {
    transform: translateX(-50%) translateY(-5px);
  }
}

// Section polaroids and quotes for pages 2 and 3
.section-polaroid {
  position: absolute;
  background: white;
  padding: 12px 12px 35px 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  filter: sepia(0.15) contrast(1.05);
  z-index: 5;
  width: 200px;
  height: 160px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: grayscale(100%);
  }

  &.section-polaroid-left {
    top: 10%;
    left: -3%;
    transform: rotate(-8deg);
  }

  &.section-polaroid-right-bottom {
    bottom: 8%;
    right: -2%;
    transform: rotate(6deg);
  }
}

.section-quote {
  position: absolute;
  background-color: var(--v-theme-background);
  border: 12px solid white;
  padding: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  font-family: 'Lora', serif;
  width: 260px;
  z-index: 5;

  p {
    margin: 0;
    font-size: 14px;
    font-style: italic;
    line-height: 1.6;
    color: var(--v-theme-gray600);
  }

  &.section-quote-right {
    top: 15%;
    right: -1%;
    transform: rotate(3deg);
  }

  &.section-quote-left-bottom {
    bottom: 12%;
    left: -2%;
    transform: rotate(-4deg);
  }
}

// What Section — light background
.what-section {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  background: #ffffff;
  padding: 60px 0 80px;
  position: relative;
  overflow: hidden;
}

.what-container {
  max-width: 1100px;
}

// Left column content
.what-left-content {
  display: flex;
  flex-direction: column;
}

.what-header {
  margin-bottom: 40px;
  padding-top: 20px;
  text-align: left;
}

.what-intro {
  font-size: 1.15rem;
  line-height: 1.8;
  color: var(--v-theme-gray600);
  max-width: 500px;
  text-align: left;
}

// Overlapping photo and quote wrapper
.what-overlap-wrapper {
  margin-top: 20px;
  width: 100%;
}

// Right column content
.what-right-content {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 40px;
}

.section-title {
  font-family: var(--v-theme-headingFont);
  font-size: 2.5rem;
  color: $primary;
  margin-bottom: 24px;
}

.section-description {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--v-theme-gray600);
  margin-bottom: 16px;
}

// Overlapping tablet and text layout
.what-content-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 450px;
}

.tablet-mockup-angled {
  position: relative;
  z-index: 10;
  transform: rotate(-3deg);
}

.tablet-frame {
  background: var(--v-theme-gray600);
  border-radius: 20px;
  padding: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 320px;

  .tablet-screen {
    background: white;
    border-radius: 4px;
    overflow: hidden;
  }

  .tablet-content {
    width: 100%;
    height: auto;
    display: block;
  }
}

.what-text-overlay {
  position: relative;
  z-index: 15;
  background: rgba(255, 255, 255, 0.95);
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  max-width: 350px;
  margin-top: 80px;
  transform: rotate(2deg);

  p {
    font-size: 1rem;
    line-height: 1.7;
    color: var(--v-theme-gray600);
    margin-bottom: 0;
  }
}

.tablet-mockup-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.tablet-mockup {
  background: var(--v-theme-gray600);
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;

  .tablet-screen {
    background: white;
    border-radius: 4px;
    overflow: hidden;
  }

  .tablet-content {
    width: 100%;
    height: auto;
    display: block;
  }
}

// How Section — dark background
.how-section {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  background: #1C1C1C;
  padding: 60px 0 80px;
  position: relative;
  overflow: hidden;
}

// Text overrides for dark how-section background
.how-section .section-title {
  color: #FAF7F4;
}

.how-section .how-intro {
  color: #A8A89A;
}

.how-section .scroll-indicator .v-icon {
  color: #A8A89A;
}

.how-container {
  max-width: 1100px;
}

// Left column content
.how-left-content {
  display: flex;
  flex-direction: column;
}

.how-header {
  margin-bottom: 30px;
  padding-top: 20px;
  text-align: left;
}

.how-intro {
  font-size: 1.15rem;
  line-height: 1.8;
  color: var(--v-theme-gray600);
  max-width: 600px;
  text-align: left;
}

.start-here-wrapper {
  text-align: left;
}

.how-get-started-btn {
  background-color: #D4401A !important;
  color: #ffffff !important;
  border-color: #D4401A !important;
}

// Right column content with overlapping photo/quote
.how-right-content {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 40px;
}

.how-overlap-wrapper {
  width: 100%;
}

.feature-col {
  padding: 8px;
}

.feature-item {
  text-align: center;
  padding: 32px 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.feature-item-square {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 28px 24px;
  min-height: 220px;

  .feature-title {
    min-height: 72px;
    display: flex;
    align-items: center;
    text-align: center;
  }
}

.feature-icon {
  margin-bottom: 24px;
}

.feature-title {
  font-family: var(--v-theme-headingFont);
  font-size: 1.5rem;
  color: $primary;
  margin-bottom: 16px;
}

.feature-description {
  font-size: 1rem;
  line-height: 1.6;
  color: var(--v-theme-gray600);
}

.pricing-note {
  font-size: 0.9rem;
  color: var(--v-theme-gray600);
}

// FAQ Section
.faq-section {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  background: var(--v-theme-surface);
  padding: 60px 0 80px;
  position: relative;
  overflow: hidden;
}

.faq-container {
  max-width: 1100px;
}

.faq-left-content {
  display: flex;
  flex-direction: column;
}

.faq-header {
  margin-bottom: 30px;
  padding-top: 20px;
  text-align: left;
}

.faq-panels {
  margin-top: 20px;

  :deep(.v-expansion-panel) {
    margin-bottom: 12px;
    border: 1px solid var(--v-theme-neutral200);
    border-radius: 8px !important;

    &::before {
      box-shadow: none !important;
    }
  }

  :deep(.v-expansion-panel-title) {
    padding: 20px 24px;
    font-size: 1.1rem;
    font-weight: 500;
    color: $primary;
    min-height: 60px;
  }

  :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 24px 20px 24px;
  }
}

.faq-question {
  font-family: var(--v-theme-headingFont);
  font-size: 1.15rem;
  color: $primary;
}

.faq-answer {
  font-size: 1rem;
  line-height: 1.7;
  color: var(--v-theme-gray600);
  margin: 0;
}

.brand-n1-dark {
  color: #1a1a1a;
  font-weight: inherit;
  font-family: var(--v-theme-headingFont)!important;
}

.brand-n1-light {
  color: #FAF7F4;
  font-weight: inherit;
  font-family: var(--v-theme-headingFont)!important;
}

.brand-n2-red {
  color: #D4401A;
  font-weight: inherit;
  font-family: var(--v-theme-headingFont)!important;
}

.faq-right-content {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 40px;
}

.faq-overlap-wrapper {
  width: 100%;
}

// Shared photo grid for decorative sections
.section-photo-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  width: 100%;
}

.what-photo-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  width: 100%;
}

.photo-grid-cell {
  aspect-ratio: 1;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

// Responsive Design
@media (max-width: 960px) {
  // Positioning is now handled by JavaScript for all screen sizes
  // No need for manual positioning overrides

  .section-polaroid,
  .section-quote {
    display: none;
  }

  .hero-container {
    min-height: 100vh;
    padding: 20px;
  }

  .text-content {
    text-align: center;
    margin-bottom: 40px;
  }

  .section-title {
    font-size: 2rem;
  }

  // What section responsive
  .what-left-content {
    order: 2;
  }

  .what-right-content {
    display: none; // Hide iPad/tablet on small screens
  }

  .what-header {
    text-align: center;
  }

  .what-intro {
    text-align: center;
    max-width: 100%;
  }

  .what-overlap-wrapper {
    margin-top: 30px;
  }

  .tablet-mockup-angled {
    transform: none;
  }

  .tablet-frame {
    width: 280px;
  }

  .what-content-wrapper {
    flex-direction: column;
    align-items: center;
  }

  .what-text-overlay {
    transform: none;
    margin-top: 0;
    max-width: 100%;
  }

  // How section responsive
  .how-header {
    text-align: center;
  }

  .how-intro {
    text-align: center;
    max-width: 100%;
  }

  .start-here-wrapper {
    text-align: center;
  }

  .how-right-content {
    display: none;
  }

  .feature-col {
    padding: 12px;
  }

  .feature-item {
    padding: 20px 16px;
  }

  // FAQ section responsive
  .faq-header {
    text-align: center;
  }

  .faq-right-content {
    display: none; // Hide photo and text on mobile
  }

  .faq-left-content {
    flex: 0 0 100%; // Make FAQ panels full width
    max-width: 100%;
  }

}

@include mobile {
  .hero-center {
    padding: 24px 20px;
  }

  .hero-logo {
    height: 35px;
  }

  .hero-tagline {
    font-size: 0.9rem;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .section-description {
    font-size: 1rem;
  }

  .tablet-mockup {
    max-width: 280px;
    padding: 12px;
  }

  .feature-item {
    padding: 24px 16px;
  }

  .feature-title {
    font-size: 1.25rem;
  }
}

// Narrow-screen stack adjustments — must come after @include mobile (max-width: 600px)
// to override its hero-logo height rule
@media (max-width: 450px) {
  .hero-center {
    padding: 20px 24px;
  }

  // Allow natural height when brand name wraps to two lines
  .hero-logo {
    height: auto;
  }
}


</style>
